import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Selector from '../../components/forms/Selector';
import IconSave from '../../components/icons/IconSave';
import IconDelete from '../../components/icons/IconDelete';
import IconAdd from '../../components/icons/IconAdd';
import language from '../../variables/language';
import PlannedEventRuns from './PlannedEventRuns';
import '../../css/modals.css';
import '../../css/phrase-composer.css';

const url = '/api/student/plannedevents';

const HOURS = Array.from({length:24}, (_, i) => String(i).padStart(2,'0'));
// 5-minute steps keep the dropdown short enough to fit without scrolling.
const MINUTES = Array.from({length:12}, (_, i) => String(i * 5).padStart(2,'0'));
// outside 09:00-20:00 is flagged red, matching the "before 9 / after 20" rule
const hourColorClass = (h) => {
   const n = Number(h);
   return (n < 9 || n > 20) ? 'time-red' : 'time-green';
}
// both dropdowns use the same cap so they always pick the same open direction
const TIME_LIST_MAX_HEIGHT = 200;

// a native <select>'s popup can't be restyled and picks its own open
// direction per element (which is why the hour list, being taller, could
// flip open upward while the shorter minute list stayed down) - this is a
// small custom dropdown instead, so both always match and options can carry
// their own colors. The list is rendered via a portal straight onto <body>:
// .modal-window has backdrop-filter set, which (like transform/filter) makes
// it a new containing block for position:fixed descendants, so a plain fixed
// div here would actually be positioned relative to the modal, not the
// viewport - a portal sidesteps that entirely.
function TimeOptionSelect({ value, onChange, options, colorClass, placeholder }) {
   const [open, setOpen] = useState(false);
   const [coords, setCoords] = useState(null);
   const wrapRef = useRef(null);
   const listRef = useRef(null);

   useEffect(() => {
      if(!open) return;
      const handleOutside = (e) => {
         const inTrigger = wrapRef.current && wrapRef.current.contains(e.target);
         const inList = listRef.current && listRef.current.contains(e.target);
         if(!inTrigger && !inList) setOpen(false);
      };
      const handleReflow = (e) => {
         // scrolling *inside* the option list itself (to reach hour 22 etc.)
         // must not close it - only close when something outside moves.
         if(listRef.current && e.target && listRef.current.contains(e.target)) return;
         setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      window.addEventListener('scroll', handleReflow, true);
      window.addEventListener('resize', handleReflow);
      return () => {
         document.removeEventListener('mousedown', handleOutside);
         window.removeEventListener('scroll', handleReflow, true);
         window.removeEventListener('resize', handleReflow);
      };
   }, [open]);

   const handleToggle = () => {
      if(!open && wrapRef.current) {
         const rect = wrapRef.current.getBoundingClientRect();
         const spaceBelow = window.innerHeight - rect.bottom;
         const openUp = spaceBelow < TIME_LIST_MAX_HEIGHT && rect.top > spaceBelow;
         setCoords({
            left: rect.left,
            width: rect.width,
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? (window.innerHeight - rect.top + 4) : undefined,
         });
      }
      setOpen(o => !o);
   }

   const handlePick = (opt) => {
      onChange(opt);
      setOpen(false);
   }

   return (
      <div className='planned-time-select' ref={wrapRef}>
         <div className={'planned-time-select-trigger' + (open ? ' open' : '')} onClick={handleToggle}>
            <span className={'planned-time-select-value' + (value ? (colorClass ? ' ' + colorClass(value) : '') : ' planned-time-select-placeholder')}>
               {value || placeholder}
            </span>
         </div>
         {open && coords ? createPortal(
            <div className='planned-time-select-list' ref={listRef} style={{...coords, maxHeight: TIME_LIST_MAX_HEIGHT}}>
               {options.map(opt => (
                  <div key={opt}
                     className={'planned-time-select-option' + (opt === value ? ' selected' : '') + (colorClass ? ' ' + colorClass(opt) : '')}
                     onClick={() => handlePick(opt)}>
                     {opt}
                  </div>
               ))}
            </div>,
            document.body
         ) : ''}
      </div>
   );
}

const resolveValue = (val) => {
   if(val && typeof val === 'object' && val.id !== undefined) {
      return String(val.id);
   }
   if(val === null || val === undefined || val === 'null' || val === 'undefined') {
      return '';
   }
   return String(val);
}

// event type ids whose scheduled work actually reads the test-generation
// override fields below (see PlanedEventService.doWorkByEventType on the
// backend - typeId 1/2 = "Easy test generation"/"Ai test generation", 3 =
// plain "Message" and never looks at them) - the section only makes sense
// to show for those two types.
const TEST_GEN_EVENT_TYPE_IDS = ['1', '2'];
// Handled as an explicit typed block in handleSave (numbers/booleans need
// real JS types, not the raw strings every other field here is stored as) -
// listed here just so the generic save loop skips them.
const TEST_GEN_KEYS = ['wordSourceMode', 'dateRangeDays', 'questionCount', 'difficulty', 'optionsPerQuestion', 'textAnswerRatio', 'autoNaming'];

const emptyValues = () => ({
   planedStudentEventType:'', isActive:'', dateTime:'', message:'',
   wordSourceMode:'', dateRangeDays:'', questionCount:'', difficulty:'',
   optionsPerQuestion:'', textAnswerRatio:'', autoNaming:'',
});

export default function PlannedEventDetailModal({ isOpen, eventId, onClose, onSaved, onDeleted }) {
   const navigate = useNavigate();
   const [values, setValues] = useState(emptyValues());
   const [initialValues, setInitialValues] = useState(emptyValues());
   const [isCycled, setIsCycled] = useState(0);
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [weekdays, setWeekdays] = useState([]);
   const [newScheduleMode, setNewScheduleMode] = useState('FIXED');
   const [newWeekDay, setNewWeekDay] = useState('');
   const [newHour, setNewHour] = useState('');
   const [newMinute, setNewMinute] = useState('');
   const [newOffsetHour, setNewOffsetHour] = useState('');
   const [newOffsetMinute, setNewOffsetMinute] = useState('');
   const [addingDay, setAddingDay] = useState(false);
   // System-wide QuizGenerationConfig - fetched once (cheap, singleton row)
   // purely so the "use default" placeholders/options below can show what
   // the default actually resolves to (e.g. "За замовчуванням (8)") instead
   // of a bare, unhelpful "default" with no number attached.
   const [globalConfig, setGlobalConfig] = useState(null);

   const loadEvent = () => {
      setLoading(true);
      axios.get(url + '/' + eventId, { withCredentials: true })
         .then(response => {
            const data = response.data || {};
            const flat = {
               planedStudentEventType: resolveValue(data.planedStudentEventType),
               isActive: data.isActive === 1 ? '1' : (data.isActive === 2 ? '2' : ''),
               dateTime: resolveValue(data.dateTime),
               message: resolveValue(data.message),
               // null on any of these (never overridden on this event) ->
               // blank, rendered as "use the global default" - see
               // QuizGenerationConfig/TEST_GEN_SECTION below.
               wordSourceMode: data.wordSourceMode || '',
               dateRangeDays: data.dateRangeDays != null ? String(data.dateRangeDays) : '',
               questionCount: data.questionCount != null ? String(data.questionCount) : '',
               difficulty: data.difficulty || '',
               optionsPerQuestion: data.optionsPerQuestion != null ? String(data.optionsPerQuestion) : '',
               textAnswerRatio: data.textAnswerRatio != null ? String(data.textAnswerRatio) : '',
               autoNaming: data.autoNaming === true ? 'true' : (data.autoNaming === false ? 'false' : ''),
            };
            setValues(flat);
            setInitialValues(flat);
            setIsCycled(data.isCycled);
         })
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); })
         .finally(() => setLoading(false));
   }
   const loadWeekdays = () => {
      axios.get(url + '/weekdays/' + eventId, { withCredentials: true })
         .then(response => setWeekdays(Array.isArray(response.data) ? response.data : []))
         .catch(() => setWeekdays([]));
   }

   useEffect(() => {
      if(!isOpen) {
         return;
      }
      axios.get('/api/quiz-generation-config', { withCredentials: true })
         .then(response => setGlobalConfig(response.data))
         .catch(() => {});
   }, [isOpen]);

   useEffect(() => {
      if(!isOpen || !eventId) {
         return;
      }
      loadEvent();
      loadWeekdays();
      setNewScheduleMode('FIXED');
      setNewWeekDay('');
      setNewHour('');
      setNewMinute('');
      setNewOffsetHour('');
      setNewOffsetMinute('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, eventId]);

   const isDirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(initialValues), [values, initialValues]);

   if(!isOpen) return '';

   const lang = language[language.lang];
   const plang = lang.plannedEvents;
   const t = lang.quizGenerationConfigPage;
   const stopProp = e => e.stopPropagation();
   const isTestGenType = TEST_GEN_EVENT_TYPE_IDS.includes(values.planedStudentEventType);

   // "За замовчуванням" alone doesn't say what the default actually is -
   // append the live global value in parentheses once it's loaded, e.g.
   // "За замовчуванням (8)". Falls back to the bare label while globalConfig
   // is still loading/unavailable rather than blocking the field.
   const defaultLabel = (formattedValue) => {
      if(!globalConfig || formattedValue === null || formattedValue === undefined || formattedValue === '') {
         return plang.useGlobalDefault;
      }
      return plang.useGlobalDefault + ' (' + formattedValue + ')';
   }
   const wordSourceModeDefaultLabel = defaultLabel(globalConfig
      ? (globalConfig.wordSourceMode === 'DATE_RANGE' ? t.sourceModeDateRange : t.sourceModeLastLesson) : null);
   const difficultyDefaultLabel = defaultLabel(globalConfig
      ? ({EASY: t.difficultyEasy, MEDIUM: t.difficultyMedium, HARD: t.difficultyHard}[globalConfig.difficulty] || t.difficultyMedium)
      : null);
   const autoNamingDefaultLabel = defaultLabel(globalConfig ? (globalConfig.autoNaming ? lang.Yes : lang.No) : null);

   const handleFieldChange = (name, value) => {
      setValues(prev => ({...prev, [name]: value}));
   }
   const handleDiscard = () => setValues(initialValues);

   const handleSave = () => {
      if(!isDirty || saving) return;
      setSaving(true);
      const payload = {};
      Object.keys(values).forEach(key => {
         if(key === 'dateTime' && isCycled === 1) return;
         if(TEST_GEN_KEYS.includes(key)) return;
         if(values[key] !== '') payload[key] = values[key];
      });
      // typed explicitly (backend fields are Integer/Boolean, not strings) -
      // blank stays omitted, which the backend's update() leaves untouched
      // (a field that was never overridden simply stays null/inherited; see
      // the known "can't revert an already-set override back to blank"
      // limitation noted where this section renders below).
      if(values.wordSourceMode !== '') payload.wordSourceMode = values.wordSourceMode;
      if(values.dateRangeDays !== '') payload.dateRangeDays = Number(values.dateRangeDays);
      if(values.questionCount !== '') payload.questionCount = Number(values.questionCount);
      if(values.difficulty !== '') payload.difficulty = values.difficulty;
      if(values.optionsPerQuestion !== '') payload.optionsPerQuestion = Number(values.optionsPerQuestion);
      if(values.textAnswerRatio !== '') payload.textAnswerRatio = Number(values.textAnswerRatio);
      if(values.autoNaming !== '') payload.autoNaming = values.autoNaming === 'true';
      axios.put(url + '/' + eventId, JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         loadEvent();
         onSaved();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setSaving(false));
   }

   const handleDeleteEvent = () => {
      if(!window.confirm(plang.deleteConfirm)) return;
      axios.delete(url + '/' + eventId, { withCredentials: true })
         .then(() => onDeleted())
         .catch(err => console.log(err));
   }

   const handleAddWeekday = () => {
      if(addingDay) return;
      if(newScheduleMode === 'AFTER_LESSON' || newScheduleMode === 'BEFORE_LESSON') {
         if(newOffsetHour === '' || newOffsetMinute === '') return;
         setAddingDay(true);
         axios.post(url + '/weekdays', JSON.stringify({
            scheduleMode: newScheduleMode,
            offsetHours: Number(newOffsetHour),
            offsetMinutes: Number(newOffsetMinute),
            planedStudentEvent: String(eventId),
         }), {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
         }).then(() => {
            setNewOffsetHour('');
            setNewOffsetMinute('');
            loadWeekdays();
         }).catch(err => console.log(err))
           .finally(() => setAddingDay(false));
         return;
      }
      if(!newWeekDay || newHour === '' || newMinute === '') return;
      setAddingDay(true);
      const time = newHour + ':' + newMinute;
      axios.post(url + '/weekdays', JSON.stringify({ scheduleMode: 'FIXED', weekDay: newWeekDay, time, planedStudentEvent: String(eventId) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setNewWeekDay('');
         setNewHour('');
         setNewMinute('');
         loadWeekdays();
      }).catch(err => console.log(err))
        .finally(() => setAddingDay(false));
   }
   const handleRemoveWeekday = (dayId) => {
      axios.delete(url + '/weekdays/' + dayId, { withCredentials: true })
         .then(() => loadWeekdays())
         .catch(err => console.log(err));
   }

   const sortedWeekdays = weekdays.slice().sort((a,b) => a.weekDay - b.weekDay || (a.time > b.time ? 1 : -1));

   // "+2 год 30 хв після заняття" - short relative-offset chip label, omitting
   // whichever of hours/minutes is zero so a pure-minutes or pure-hours offset
   // doesn't read as "+2 год 0 хв".
   const formatOffsetShort = (hours, minutes) => {
      const parts = [];
      if(hours) parts.push(hours + ' ' + lang.hoursShort);
      if(minutes) parts.push(minutes + ' ' + lang.minutesShort);
      if(parts.length === 0) return plang.immediatelyAfterLesson;
      return '+' + parts.join(' ') + ' ' + plang.afterLessonShort;
   }

   // mirror of formatOffsetShort for BEFORE_LESSON - "-30 хв до заняття".
   const formatOffsetBeforeShort = (hours, minutes) => {
      const parts = [];
      if(hours) parts.push(hours + ' ' + lang.hoursShort);
      if(minutes) parts.push(minutes + ' ' + lang.minutesShort);
      if(parts.length === 0) return plang.immediatelyBeforeLesson;
      return '-' + parts.join(' ') + ' ' + plang.beforeLessonShort;
   }

   return (
      <div className="modal-menu" onClick={onClose} style={{ display: 'flex' }}>
         <div className="modal-window lesson-detail-window" onClick={stopProp}>
            <div className='lesson-detail-header'>
               <h3 className="modal-w-title lesson-detail-title">{lang['Config planed event']}</h3>
               <div className='lesson-detail-delete-btn' onClick={handleDeleteEvent}>
                  <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
               </div>
            </div>

            {loading ? (
               <div className='event-edit-loading'>{lang['Loading ...']}</div>
            ) : (
               <div className='lesson-detail-body'>
                  <div className='lesson-detail-form-col'>
                     <div className='student-card-section'>
                        <div className='student-card-fields-grid m-block'>
                           <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.planedStudentEventType}>
                              <Selector className='submit-field form-selection txt-c'
                                 name='planedStudentEventType' url='/api/student/plannedevents/types'
                                 value={values.planedStudentEventType} disabled={false}
                                 selectorsHundler={(value) => handleFieldChange('planedStudentEventType', value)} />
                           </div>
                        </div>
                        <div className='student-card-fields-grid m-block'>
                           <div className='flex-from-input-wraper student-card-field' data-label={lang.isActive}>
                              <Selector className='submit-field form-selection txt-c'
                                 name='isActive' url='/api/booleans' value={values.isActive} disabled={false}
                                 selectorsHundler={(value) => handleFieldChange('isActive', value)} />
                           </div>
                           {isCycled === 1 ? (
                              <div className='flex-from-input-wraper student-card-field planned-readonly-field m-block' data-label={lang.isCycled}>
                                 <span className='student-lessons-badge on'>
                                    <i className='student-lessons-badge-dot'></i>{lang.Yes}
                                 </span>
                              </div>
                           ) : (
                              <div className='flex-from-input-wraper student-card-field m-block' data-label={plang.when}>
                                 <input className='submit-field from-input txt-c' type='datetime-local'
                                    value={values.dateTime && values.dateTime.length >= 16 ? values.dateTime.slice(0,16) : values.dateTime}
                                    onChange={(e) => handleFieldChange('dateTime', e.target.value)} />
                              </div>
                           )}
                        </div>
                        <div className='student-card-fields-grid'>
                           <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.message}>
                              <textarea className='submit-field from-input txt-c event-edit-textarea'
                                 placeholder={lang.message}
                                 value={values.message}
                                 onChange={(e) => handleFieldChange('message', e.target.value)} />
                           </div>
                        </div>

                        {isTestGenType ? (
                           <>
                              <p className='student-card-section-title'>{plang.testGenSectionTitle}</p>
                              <p className='planned-testgen-hint'>{plang.testGenHint}</p>
                              <div className='student-card-fields-grid m-block'>
                                 <div className='flex-from-input-wraper student-card-field' data-label={plang.wordSourceModeLabel}>
                                    <select className='submit-field form-selection txt-c' value={values.wordSourceMode}
                                       onChange={(e) => handleFieldChange('wordSourceMode', e.target.value)}>
                                       <option value=''>{wordSourceModeDefaultLabel}</option>
                                       <option value='LAST_LESSON'>{t.sourceModeLastLesson}</option>
                                       <option value='DATE_RANGE'>{t.sourceModeDateRange}</option>
                                    </select>
                                 </div>
                                 {values.wordSourceMode === 'DATE_RANGE' ? (
                                    <div className='flex-from-input-wraper student-card-field' data-label={t.dateRangeDays}>
                                       <input className='submit-field from-input txt-c' type='number' min='1'
                                          placeholder={defaultLabel(globalConfig ? globalConfig.dateRangeDays : null)} value={values.dateRangeDays}
                                          onChange={(e) => handleFieldChange('dateRangeDays', e.target.value)} />
                                    </div>
                                 ) : ''}
                                 <div className='flex-from-input-wraper student-card-field' data-label={t.questionCount}>
                                    <input className='submit-field from-input txt-c' type='number' min='1'
                                       placeholder={defaultLabel(globalConfig ? globalConfig.questionCount : null)} value={values.questionCount}
                                       onChange={(e) => handleFieldChange('questionCount', e.target.value)} />
                                 </div>
                                 <div className='flex-from-input-wraper student-card-field' data-label={t.optionsPerQuestion}>
                                    <input className='submit-field from-input txt-c' type='number' min='2'
                                       placeholder={defaultLabel(globalConfig ? globalConfig.optionsPerQuestion : null)} value={values.optionsPerQuestion}
                                       onChange={(e) => handleFieldChange('optionsPerQuestion', e.target.value)} />
                                 </div>
                                 <div className='flex-from-input-wraper student-card-field' data-label={t.difficulty}>
                                    <select className='submit-field form-selection txt-c' value={values.difficulty}
                                       onChange={(e) => handleFieldChange('difficulty', e.target.value)}>
                                       <option value=''>{difficultyDefaultLabel}</option>
                                       <option value='EASY'>{t.difficultyEasy}</option>
                                       <option value='MEDIUM'>{t.difficultyMedium}</option>
                                       <option value='HARD'>{t.difficultyHard}</option>
                                    </select>
                                 </div>
                                 <div className='flex-from-input-wraper student-card-field' data-label={t.textAnswerRatio}>
                                    <input className='submit-field from-input txt-c' type='number' min='0' max='100'
                                       placeholder={defaultLabel(globalConfig ? globalConfig.textAnswerRatio : null)} value={values.textAnswerRatio}
                                       onChange={(e) => handleFieldChange('textAnswerRatio', e.target.value)} />
                                 </div>
                                 <div className='flex-from-input-wraper student-card-field' data-label={t.autoNaming}>
                                    <select className='submit-field form-selection txt-c' value={values.autoNaming}
                                       onChange={(e) => handleFieldChange('autoNaming', e.target.value)}>
                                       <option value=''>{autoNamingDefaultLabel}</option>
                                       <option value='true'>{lang.Yes}</option>
                                       <option value='false'>{lang.No}</option>
                                    </select>
                                 </div>
                              </div>
                           </>
                        ) : ''}
                     </div>

                     {isDirty ? (
                        <div className='student-card-form-footer'>
                           <span className='student-card-unsaved-hint'>{lang.unsavedChanges}</span>
                           <div className='student-card-discard-link' onClick={handleDiscard}>{lang.cancel}</div>
                           <button type='button' className='student-card-save-btn' disabled={saving} onClick={handleSave}>
                              <IconSave />
                              {saving ? lang['Loading ...'] : lang.save}
                           </button>
                        </div>
                     ) : ''}
                  </div>

                  {(isCycled === 1 || isTestGenType) ? (
                     <div className='lesson-detail-participants-col'>
                        {isCycled === 1 ? (
                           <>
                              <p className='student-card-section-title'>{plang.scheduleSectionTitle}</p>
                              <div className='lesson-detail-participants-list'>
                                 <div className='planned-schedule-chips'>
                                    {sortedWeekdays.map(d => (
                                       <span className='student-lessons-badge planned-weekday-chip' key={d.id}>
                                          {d.scheduleMode === 'AFTER_LESSON' ? formatOffsetShort(d.offsetHours, d.offsetMinutes)
                                             : d.scheduleMode === 'BEFORE_LESSON' ? formatOffsetBeforeShort(d.offsetHours, d.offsetMinutes)
                                             : lang.weekDaysShort[d.weekDay - 1] + ' ' + String(d.time).slice(0,5)}
                                          <span className='planned-weekday-chip-remove' onClick={() => handleRemoveWeekday(d.id)}>×</span>
                                       </span>
                                    ))}
                                    {sortedWeekdays.length === 0 ? <p className='lesson-detail-empty'>{plang.noSchedule}</p> : ''}
                                 </div>
                              </div>
                              <p className='student-card-section-title'>{lang['Add day and time']}</p>
                              <div className='phrase-type-tabs'>
                                 <span className={'phrase-type-tab' + (newScheduleMode === 'FIXED' ? ' active' : '')}
                                    onClick={() => setNewScheduleMode('FIXED')}>{plang.scheduleModeFixed}</span>
                                 <span className={'phrase-type-tab' + (newScheduleMode === 'AFTER_LESSON' ? ' active' : '')}
                                    onClick={() => setNewScheduleMode('AFTER_LESSON')}>{plang.scheduleModeAfterLesson}</span>
                                 <span className={'phrase-type-tab' + (newScheduleMode === 'BEFORE_LESSON' ? ' active' : '')}
                                    onClick={() => setNewScheduleMode('BEFORE_LESSON')}>{plang.scheduleModeBeforeLesson}</span>
                              </div>
                              {newScheduleMode === 'FIXED' ? (
                                 <div className='lesson-detail-add-row'>
                                    <div className='planned-add-field planned-add-field-grow'>
                                       <span className='planned-add-field-label'>{lang.weekDay}</span>
                                       <Selector className='submit-field form-selection txt-c'
                                          name='weekDay' url='/api/weekdays' value={newWeekDay} disabled={false}
                                          selectorsHundler={(value) => setNewWeekDay(value)} />
                                    </div>
                                    <div className='planned-add-field'>
                                       <span className='planned-add-field-label'>{plang.hour}</span>
                                       <TimeOptionSelect value={newHour} onChange={setNewHour} options={HOURS}
                                          colorClass={hourColorClass} placeholder='--' />
                                    </div>
                                    <span className='planned-time-picker-sep'>:</span>
                                    <div className='planned-add-field'>
                                       <span className='planned-add-field-label'>{plang.minute}</span>
                                       <TimeOptionSelect value={newMinute} onChange={setNewMinute} options={MINUTES}
                                          placeholder='--' />
                                    </div>
                                    <div className='student-lessons-add-btn' onClick={handleAddWeekday} title={lang['Add day and time']}>
                                       <IconAdd />
                                    </div>
                                 </div>
                              ) : (
                                 <div className='lesson-detail-add-row'>
                                    <span className='planned-offset-hint'>
                                       {newScheduleMode === 'BEFORE_LESSON' ? plang.offsetBeforeHint : plang.offsetHint}
                                    </span>
                                    <div className='planned-add-field'>
                                       <span className='planned-add-field-label'>{plang.hour}</span>
                                       <TimeOptionSelect value={newOffsetHour} onChange={setNewOffsetHour} options={HOURS}
                                          placeholder='--' />
                                    </div>
                                    <span className='planned-time-picker-sep'>:</span>
                                    <div className='planned-add-field'>
                                       <span className='planned-add-field-label'>{plang.minute}</span>
                                       <TimeOptionSelect value={newOffsetMinute} onChange={setNewOffsetMinute} options={MINUTES}
                                          placeholder='--' />
                                    </div>
                                    <div className='student-lessons-add-btn' onClick={handleAddWeekday} title={lang['Add day and time']}>
                                       <IconAdd />
                                    </div>
                                 </div>
                              )}
                           </>
                        ) : ''}
                        {isTestGenType ? <PlannedEventRuns eventId={eventId} /> : ''}
                     </div>
                  ) : ''}
               </div>
            )}
         </div>
      </div>
   );
}
